import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCopy, faImage, faPlay } from '@fortawesome/free-solid-svg-icons';
import styles from '../PromptGenerator.module.css';
import { useNotification } from '../../../context/NotificationContext';

interface ResultsViewerProps {
  generatedPrompts: string[];
  isGenerating: boolean;
  handleReset: () => void;
  handleGenerateImageFromPrompt: (promptText: string, index: number) => void;
  shouldShowGenerateImageButton: (promptTitle: string) => boolean;
  userIdea: string;
}

const ResultsViewer: React.FC<ResultsViewerProps> = ({
  generatedPrompts,
  isGenerating,
  handleReset,
  handleGenerateImageFromPrompt,
  shouldShowGenerateImageButton,
  userIdea
}) => {
  const { t } = useTranslation('promptGenerator');
  const { showNotification } = useNotification();
  
  const handleCopyIndividualPrompt = async (prompt: string, index: number) => {
    try {
      await navigator.clipboard.writeText(prompt);
      showNotification({ 
        title: t('promptGenerator.notifications.copied.title'), 
        message: t('promptGenerator.notifications.copied.message'), 
        type: 'success' 
      });
    } catch (error) {
      showNotification({ title: 'Copy failed', message: 'Could not copy to clipboard', type: 'error' });
    }
  };

  const handleCopyAllPrompts = async () => {
    const textToCopy = generatedPrompts.join('\n\n---\n\n');
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification({ 
        title: t('promptGenerator.notifications.copied.title'), 
        message: 'All prompts copied to clipboard', 
        type: 'success' 
      });
    } catch (error) {
      showNotification({ title: 'Copy failed', message: 'Could not copy all prompts', type: 'error' });
    }
  };

  const isBatch = generatedPrompts.length > 1;

  if (isGenerating) {
    return (
      <div className={styles.resultsSection}>
        <div className={styles.promptContainer}>
          <div className={styles.loadingPrompt}>
            <div className={styles.loadingDots}>
              <span />
              <span />
              <span />
            </div>
            <p>{t('promptGenerator.buttons.generating')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resultsSection}>
      <div className={styles.resultsHeader}>
        <h4 className={styles.resultsTitle}>{t('promptGenerator.results.title')}</h4>
      </div>
      
      <div className={styles.promptContainer}>
        {isBatch ? (
          <div className={styles.promptCardsContainer}>
            <div className={styles.batchHeader}>
              <span className={styles.batchCount}>
                {generatedPrompts.length} {t('promptGenerator.results.promptNumber')}
                {generatedPrompts.length !== 1 ? 's' : ''}
              </span>
              <button
                className={styles.copyAllButton}
                onClick={handleCopyAllPrompts}
                title="Copy all prompts"
              >
                <FontAwesomeIcon icon={faCopy} />
                {t('promptGenerator.buttons.copy')} All
              </button>
            </div>
            <div className={styles.promptCards}>
              {generatedPrompts.map((prompt, index) => (
                <div key={index} className={styles.promptCard}>
                  <div className={styles.cardHeader}>
                    <h5 className={styles.cardTitle}>
                      {t('promptGenerator.results.promptNumber')} {index + 1}
                    </h5>
                    <button
                      className={styles.cardCopyButton}
                      onClick={() => handleCopyIndividualPrompt(prompt, index)}
                      title={`Copy prompt ${index + 1}`}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                    {shouldShowGenerateImageButton(userIdea) && (
                      <button
                        className={styles.generateImageButton}
                        onClick={() => handleGenerateImageFromPrompt(prompt, index)}
                        title={t('promptGenerator.buttons.generateImage')}
                      >
                        <FontAwesomeIcon icon={faImage} />
                        {t('promptGenerator.buttons.generateImage')}
                      </button>
                    )}
                  </div>
                  <div className={styles.cardContent}>
                    <p>{prompt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.promptDisplay}>
            <p>{generatedPrompts[0]}</p>
          </div>
        )}
      </div>

      <div className={styles.bottomActions}>
        <button
          className={styles.secondaryButton}
          onClick={handleReset}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{marginRight: '0.5rem'}} />
          Create Another
        </button>
        {/* TODO: Implement "Continue to Generation" workflow
        <button
          className={styles.primaryButton}
          // Placeholder action to continue to next step in workflow
        >
          <FontAwesomeIcon icon={faPlay} style={{marginRight: '0.5rem'}} />
          Continue to Generation
        </button>
        */}
      </div>
    </div>
  );
};

export default ResultsViewer;